/** Keep in sync with server/src/rules/document-rule-exception.logic.ts */

const AMOUNT = 'AMOUNT';
const DOCUMENT_CODE = 'DOCUMENT_CODE';
const GT = 'GT';
const LT = 'LT';
const EQ = 'EQ';
const AND = 'AND';
const OR = 'OR';

function resolveJoinOperator(filter) {
  const raw = String(filter?.joinOperator || AND).trim().toUpperCase();
  return raw === OR ? OR : AND;
}

function isValidExceptionAmount(amount) {
  return Number.isFinite(amount) && amount > 0;
}

function normalizeStoredExceptionAmount(amount) {
  const abs = Math.abs(Number(amount));
  if (!Number.isFinite(abs) || abs <= 0) return abs;
  return Math.round(abs * 100) / 100;
}

function isCompleteDocumentExceptionFilter(filter) {
  if (!filter) return false;
  const kind = String(filter.kind || '').trim();
  if (kind === DOCUMENT_CODE) {
    return String(filter.value ?? '').trim().length > 0;
  }
  if (kind === AMOUNT) {
    const operator = String(filter.operator || '').trim();
    const amount = normalizeStoredExceptionAmount(Number(filter.amount));
    return [GT, LT, EQ].includes(operator) && isValidExceptionAmount(amount);
  }
  return false;
}

function evaluateAmountException(documentAmount, filterAmount, operator) {
  const absAmount = Math.abs(Number(documentAmount));
  const target = Math.abs(Number(filterAmount));
  if (!Number.isFinite(absAmount) || !Number.isFinite(target)) return false;
  if (operator === GT) return absAmount > target;
  if (operator === LT) return absAmount < target;
  if (operator === EQ) return absAmount === target;
  return false;
}

function documentExceptionFilterMatches(ctx, filter) {
  const kind = String(filter.kind || '').trim();
  if (kind === DOCUMENT_CODE) {
    const expected = String(filter.value ?? '').trim();
    if (!expected) return false;
    const actual = ctx.documentCode != null ? String(ctx.documentCode).trim() : '';
    return actual === expected;
  }
  if (kind === AMOUNT) {
    const operator = String(filter.operator || '').trim();
    const amount = normalizeStoredExceptionAmount(Number(filter.amount));
    if (![GT, LT, EQ].includes(operator) || !isValidExceptionAmount(amount)) return false;
    const docAmount = Number(ctx.originalAmount);
    if (!Number.isFinite(docAmount)) return false;
    return evaluateAmountException(docAmount, amount, operator);
  }
  return false;
}

function documentExceptionRowMatches(ctx, filters) {
  if (!Array.isArray(filters) || filters.length === 0) return false;
  const complete = filters.filter((filter) => isCompleteDocumentExceptionFilter(filter));
  if (complete.length === 0) return false;

  let result = documentExceptionFilterMatches(ctx, complete[0]);
  for (let i = 1; i < complete.length; i += 1) {
    const matched = documentExceptionFilterMatches(ctx, complete[i]);
    if (resolveJoinOperator(complete[i]) === OR) {
      result = result || matched;
    } else {
      result = result && matched;
    }
  }
  return result;
}

export function buildDocumentExceptionContext(document) {
  const meta = document?.meta || {};
  return {
    originalAmount: meta.originalAmount ?? meta.amount ?? null,
    documentCode: meta.documentCode ?? null,
  };
}

/** First documentException row whose filters match the document, or null. */
export function findMatchingDocumentException(rule, document) {
  if (!rule || !document) return null;
  const exceptions = rule.documentExceptions;
  if (!Array.isArray(exceptions) || exceptions.length === 0) return null;
  const ctx = buildDocumentExceptionContext(document);
  for (const row of exceptions) {
    if (documentExceptionRowMatches(ctx, row?.filters || [])) {
      return row;
    }
  }
  return null;
}

/**
 * MATCH-style account resolution for a DOCUMENT rule:
 * first matching exception accountKey, else rule.accountKey.
 */
export function resolveDocumentRuleAccountKey(rule, document) {
  if (!rule) return null;
  const matched = findMatchingDocumentException(rule, document);
  const fromException = String(matched?.accountKey || '').trim();
  if (fromException) return fromException;
  const fromRule = String(rule.accountKey || '').trim();
  return fromRule || null;
}
